import { zodResolver } from '@hookform/resolvers/zod';
import type { HTMLProps } from 'react';
import type { DefaultValues } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Accordion } from '~/components/Accordion';
import { FormFieldDropdown, FormFieldInput } from '~/components/FormField';
import { cn } from '~/lib/cn';
import { getI18nErrorMap } from '~/lib/getI18nErrorMap';
import { useOrganizationList } from '~/modules/settings/api/queries/organizationList';

import { ExternalPermissions } from './parts/ExternalPermissions';
import { InternalPermissions } from './parts/InternalPermissions';
import {
  type UserFormSchema,
  userFormSchema,
  type UserFormSchemaForExternalUser,
  userFormSchemaForExternalUser,
} from './schema';

const USER_TYPES = [
  { value: 'internal', label: 'Internal' },
  { value: 'external', label: 'External' },
];

type UserFormProps<TExternal extends boolean = false> = {
  isExternal?: TExternal;
  values?: TExternal extends true
    ? UserFormSchemaForExternalUser
    : UserFormSchema;
  onSubmit: (
    data: TExternal extends true
      ? UserFormSchemaForExternalUser
      : UserFormSchema,
  ) => void;
} & Omit<HTMLProps<HTMLFormElement>, 'method' | 'onSubmit'>;

export function UserForm<TExternal extends boolean = false>({
  values,
  onSubmit,
  className,
  isExternal,
  ...formProps
}: UserFormProps<TExternal>) {
  const { t } = useTranslation('settings', { keyPrefix: 'user' });
  const form = useForm<
    TExternal extends true ? UserFormSchemaForExternalUser : UserFormSchema
  >({
    defaultValues: {
      // Internal permissions should be hidden for external user
      ...(!isExternal
        ? {
            userType: 'external',
            internalPermissions: {
              roles: [{ value: 'OPS_ADMIN' }],
              labelGroups: [{ value: '1' }],
            },
          }
        : {}),

      // External permissions has to be present for both internal and external
      externalPermissions: [
        {
          role: '',
          labels: [],
        },
      ],
    } as DefaultValues<
      TExternal extends true ? UserFormSchemaForExternalUser : UserFormSchema
    >,
    values,
    resolver: zodResolver(
      isExternal ? userFormSchemaForExternalUser : userFormSchema,
      {
        errorMap: getI18nErrorMap((s) => t(`validations.${s}`)),
      },
    ),
    mode: 'onBlur',
  });

  const { organizationList } = useOrganizationList({});

  // Path gets broken with TExternal generic
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const selectedUserType = form.watch('userType' as any);

  return (
    <FormProvider {...form}>
      <form
        aria-label="user form"
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex', 'flex-col', 'gap-md', className)}
        {...formProps}
      >
        <Accordion
          defaultValue={['basicInfo', 'contactInfo', 'userPermissions']}
        >
          <Accordion.Item
            value="basicInfo"
            title={t('basicInformationTitle')}
            className="grid grid-cols-3 gap-md bg-level-02"
          >
            <FormFieldInput name="firstName" label={t('fields.firstName')} />
            <FormFieldInput name="lastName" label={t('fields.lastName')} />
            {!isExternal && (
              <FormFieldDropdown
                options={USER_TYPES}
                name="userType"
                label={t('fields.userType')}
              />
            )}
            {!isExternal && selectedUserType === 'internal' ? (
              <FormFieldDropdown
                options={
                  organizationList?.map((org) => ({
                    label: org.title,
                    value: org.id,
                  })) ?? []
                }
                name="organization"
                label={t('fields.organization')}
              />
            ) : null}
            <FormFieldInput name="jobTitle" label={t('fields.jobTitle')} />
          </Accordion.Item>
          <Accordion.Item
            value="contactInfo"
            title={t('contactInformationTitle')}
            className="grid grid-cols-3 gap-md bg-level-02"
          >
            <FormFieldInput
              name="email"
              type="email"
              label={t('fields.email')}
            />
            <FormFieldInput name="phone" type="tel" label={t('fields.phone')} />
          </Accordion.Item>
          <Accordion.Item
            value="userPermissions"
            title={t('permissionsTitle')}
            className="flex flex-col gap-md bg-level-02"
          >
            <p className="text-subtle">{t('roleLabelNotice')}</p>
            {selectedUserType === 'internal' ? (
              <InternalPermissions name="internalPermissions" />
            ) : (
              <ExternalPermissions name="externalPermissions" />
            )}
          </Accordion.Item>
        </Accordion>
      </form>
    </FormProvider>
  );
}

UserForm.displayName = 'UserForm';
